import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { DisclaimerIcon } from '../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { DynamicContent } from '../../../app-kit/components/DynamicContent/DynamicContent';
import { mapDynamicContentItems } from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import type { DynamicContentsItems } from '../../../app-kit/components/DynamicContent/types';
import { spacingHelper, typographyHelper, mediaHelper } from '@property.com.au/ui-kit';
import { mapButtonVariant } from '../../../app-kit/helpers/mapButtonVariant';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { NearbyPlanningPermitTiles } from './subcomponents/NearbyPlanningPermitTiles/NearbyPlanningPermitTiles';
import { ShowMoreButton } from './subcomponents/ShowMoreButton/ShowMoreButton';
import { ResultOf } from 'gql.tada';
import { propertyPageNearbyPlanningPermitsBrickFragment } from '../../graphql/propertyPageQuery';

type NearbyPlanningPermitsBrick = NonNullable<
  ResultOf<typeof propertyPageNearbyPlanningPermitsBrickFragment>
>;
type NearbyPlanningPermits = NonNullable<NearbyPlanningPermitsBrick['nearbyPlanningPermits']>;
type Tile = NonNullable<NearbyPlanningPermits['tiles']>[number];
type Button = NonNullable<NearbyPlanningPermits['showMoreButton']>['button'];

interface NearbyPlanningPermitsBrickProps {
  title: string;
  subtitle: string;
  tiles: Tile[];
  disclaimer: DynamicContentsItems;
  showMoreButton?: {
    button: Button;
    visibleOnMobile: boolean;
    visibleOnDesktop: boolean;
  } | null;
  mobileVisibleTilesLimit: number;
  desktopVisibleTilesLimit: number;
}
const DisclaimerWrapper = styled.div<{ $visibleOnMobile: boolean; $visibleOnDesktop: boolean }>`
  margin-top: ${spacingHelper('small')};
  > * {
    ${typographyHelper('caption')};
  }
  line-height: ${spacingHelper('medium')};

  ${mediaHelper('tabletMax')} {
    ${({ $visibleOnMobile }) => `display: ${$visibleOnMobile ? 'block' : 'none'}`};
  }

  ${mediaHelper('desktopMin')} {
    ${({ $visibleOnDesktop }) => `display: ${$visibleOnDesktop ? 'block' : 'none'}`};
  }
`;

const NearbyPlanningPermitsBrick: FunctionComponent<NearbyPlanningPermitsBrickProps> = ({
  title,
  subtitle,
  tiles,
  disclaimer,
  showMoreButton,
  mobileVisibleTilesLimit,
  desktopVisibleTilesLimit,
}) => {
  const { displayModal } = usePropertyPageModalContext();
  const [isShowMoreButtonClicked, setIsShowMoreButtonClicked] = useState<boolean>(false);
  const isShowMoreButtonVisible = !isShowMoreButtonClicked && showMoreButton;
  return (
    <ContentBrick
      title={title}
      subtitle={subtitle}
      headerChildren={
        <DisclaimerIcon
          title="Open Nearby Planning Permits Disclaimer"
          onClick={() => {
            displayModal({ name: 'nearbyPlanningPermitsDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'property_page__nearby_planning_permits___disclaimer_button',
            });
          }}
        />
      }
    >
      <NearbyPlanningPermitTiles
        tiles={tiles}
        isCollapsed={!isShowMoreButtonClicked}
        mobileVisibleTilesLimit={mobileVisibleTilesLimit}
        desktopVisibleTilesLimit={desktopVisibleTilesLimit}
      />
      {isShowMoreButtonVisible && (
        <ShowMoreButton
          $visibleOnMobile={showMoreButton.visibleOnMobile}
          $visibleOnDesktop={showMoreButton.visibleOnDesktop}
          variant={mapButtonVariant(showMoreButton.button.buttonVariant)}
          onClick={() => setIsShowMoreButtonClicked(true)}
        >
          {showMoreButton.button.text}
        </ShowMoreButton>
      )}
      <DisclaimerWrapper
        $visibleOnMobile={(isShowMoreButtonClicked || !showMoreButton?.visibleOnMobile) ?? true}
        $visibleOnDesktop={(isShowMoreButtonClicked || !showMoreButton?.visibleOnDesktop) ?? true}
      >
        <DynamicContent dynamicContents={disclaimer} textColorVariant="textSecondary" />
      </DisclaimerWrapper>
    </ContentBrick>
  );
};

export const NearbyPlanningPermitsBrickWithData: FunctionComponent = () => {
  const data = usePropertyPageQuery()?.data.propertyPageBySlugV3;
  if (!data?.nearbyPlanningPermits) return null;

  const {
    title,
    subtitle,
    tiles,
    showMoreButton,
    mobileVisibleTilesLimit,
    desktopVisibleTilesLimit,
    disclaimer,
  } = data.nearbyPlanningPermits;

  if (!tiles) return null;
  return (
    <NearbyPlanningPermitsBrick
      title={title}
      subtitle={subtitle}
      tiles={tiles}
      disclaimer={mapDynamicContentItems(disclaimer)}
      showMoreButton={showMoreButton}
      mobileVisibleTilesLimit={mobileVisibleTilesLimit}
      desktopVisibleTilesLimit={desktopVisibleTilesLimit}
    />
  );
};
